<!-- En-tête commun -->
<div class="bg-slate-100 p-3 rounded text-gray-500">
  <p class="text-sm">
    Réserver un rendez-vous avec le
    <span class="text-primary">{{ doctorName || 'Docteur' }}</span>
  </p>
  <p class="text-sm">
    le <span> {{ slot?.date | date:'EEEE d MMMM yyyy' }}</span>
    de
    <span class="text-primary font-semibold">{{ slot?.startTime | date:'HH:mm' }}</span>
    à
    <span class="text-primary font-semibold">{{ slot?.endTime | date:'HH:mm' }}</span>
  </p>
</div>

<!-- Étape 1 : Formulaire de réservation -->
@if (currentStep === 1) {
<form
  [formGroup]="appointmentForm"
  (ngSubmit)="onSubmit()"
  class="w-full mt-5 flex flex-col gap-y-5">
    <div class="flex flex-col gap-y-1 text-gray-700 w-full">
      <label for="specialisation" class="text-[13px] required w-fit">
        Choix de la spécialisation
      </label>
      <select
        name="specialisation"
        id="specialisation"
        formControlName="specialisation"
        class="select border border-gray-300 border-solid w-full">
        <option value="">Choix de la spécialisation</option>
        <option *ngFor="let specialisation of specialisations" [value]="specialisation">
          {{ specialisation }}
        </option>
      </select>
      <div
        *ngIf="appointmentForm.get('specialisation')?.errors?.['required'] && appointmentForm.get('specialisation')?.touched"
        class="text-red-500 text-xs mt-1">
        La spécialisation du médecin est requise
      </div>
    </div>

    <div class="flex flex-col gap-y-1 text-gray-700 w-full">
      <label for="reason" class="text-[13px] required w-fit">Choix du motif</label>
      <select
        name="reason"
        id="reason"
        formControlName="reason"
        class="select border border-gray-300 border-solid w-full">
        <option value="">Choix du motif</option>
        <option *ngFor="let reason of appointmentReasons" [value]="reason.label">
          {{ reason?.label }}
        </option>
      </select>
      <div
        *ngIf="appointmentForm.get('reason')?.errors?.['required'] && appointmentForm.get('reason')?.touched"
        class="text-red-500 text-xs mt-1">
        Le motif de consultation est requis
      </div>
    </div>

    <div class="flex flex-col gap-y-1 text-gray-700 w-full">
      <label for="notes" class="text-[13px] w-full">Notes</label>
      <textarea
        name="notes"
        id="notes"
        formControlName="notes"
        class="textarea border border-gray-300 border-solid w-full bg-white"
        placeholder="Notes"
        rows="3"></textarea>
    </div>

    <!-- Sélection du patient -->
    <div class="flex flex-col gap-y-1 text-gray-700 w-full">
      <label class="text-[13px] required w-fit">Pour quel patient ?</label>
      <div class="space-y-3">
        <!-- Patient principal -->
        <div *ngIf="currentUser" class="flex items-center">
          <input
            type="radio"
            id="patient-main"
            name="patient"
            [value]="currentUser.passportNumber"
            (change)="selectPatient(currentUser.passportNumber)"
            [checked]="selectedPatientId === currentUser.passportNumber"
            class="radio radio-sm" />
          <label
            for="patient-main"
            class="ml-3 block text-sm font-medium text-gray-700 cursor-pointer">
            <div class="flex items-center">
              <span class="font-semibold">
                {{ currentUser.firstName }} {{ currentUser.lastName }}
              </span>
              <span class="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                Principal
              </span>
            </div>
          </label>
        </div>

        <!-- Sous-comptes -->
        <div *ngFor="let account of subAccounts; let i = index" class="flex items-center">
          <input
            type="radio"
            [id]="'patient-' + i"
            name="patient"
            [value]="account.passportNumber"
            (change)="selectPatient(account.passportNumber)"
            [checked]="selectedPatientId === account.passportNumber"
            class="radio radio-sm" />
          <label
            [for]="'patient-' + i"
            class="ml-3 block text-sm font-medium text-gray-700 cursor-pointer">
            <div class="flex items-center">
              <span class="font-semibold">
                {{ account.firstName }} {{ account.lastName }}
              </span>
              <span
                *ngIf="account.accountType === 'UNDERCOVER'"
                class="ml-2 text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full">
                Sous-couvert
              </span>
            </div>
          </label>
        </div>

        <!-- Message si aucun patient -->
        <div *ngIf="!currentUser && subAccounts.length === 0" class="text-sm text-gray-500 italic">
          Aucun patient disponible
        </div>
      </div>
      <div
        *ngIf="appointmentForm.get('patientId')?.errors?.['required'] && appointmentForm.get('patientId')?.touched"
        class="text-red-500 text-xs mt-1">
        Veuillez sélectionner un patient
      </div>
    </div>

    <!-- Conditions d'annulation -->
    <div *ngIf="getCancellationRules().length > 0" class="mt-4 p-4 border border-orange-200 rounded-lg bg-orange-50">
      <h4 class="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <lucide-icon [img]="AlertCircleIcon" [size]="16" class="text-orange-600"></lucide-icon>
        Conditions d'annulation
      </h4>
      <div class="space-y-2 mb-3">
        <p class="text-xs text-gray-600 mb-2">En cas d'annulation, le remboursement s'effectue selon les conditions suivantes :</p>
        <ul class="space-y-1 text-xs text-gray-700">
          <li *ngFor="let rule of getCancellationRules()" class="flex items-start gap-2">
            <span class="text-orange-600 font-bold">•</span>
            <span [class]="rule.minMinutesBefore === 0 && rule.refundPercent === 0 ? 'text-red-600 font-semibold' : ''">
              {{ formatCancellationRule(rule) }}
            </span>
          </li>
        </ul>
      </div>
      <div class="flex items-start gap-2 pt-2 border-t border-orange-200">
        <input
          type="checkbox"
          id="acceptCancellationTerms"
          formControlName="acceptCancellationTerms"
          class="checkbox checkbox-sm mt-0.5" />
        <label for="acceptCancellationTerms" class="text-xs text-gray-700 cursor-pointer flex-1">
          J'accepte les conditions d'annulation et de remboursement ci-dessus
        </label>
      </div>
      <div
        *ngIf="appointmentForm.get('acceptCancellationTerms')?.errors?.['required'] && appointmentForm.get('acceptCancellationTerms')?.touched"
        class="text-red-500 text-xs mt-1">
        Vous devez accepter les conditions d'annulation pour continuer
      </div>
    </div>

  <!-- Boutons d'action étape 1 -->
  <div class="flex gap-3 pt-4 justify-between">
    <button
      type="button"
      (click)="closeModal()"
      class="text-sm px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
      Annuler
    </button>
    <button
      type="submit"
      [disabled]="isLoading || appointmentForm.invalid"
      class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm">
      <lucide-icon *ngIf="!isLoading" [img]="CheckIcon" [size]="16"></lucide-icon>
      <div *ngIf="isLoading" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
      @if (requiresDeposit()) {
        Continuer
      } @else {
        {{ isLoading ? 'Réservation...' : 'Confirmer le rendez-vous' }}
      }
    </button>
  </div>
</form>
}

<!-- Étape 2 : Récapitulatif et paiement de l'acompte -->
@if (currentStep === 2 && requiresDeposit()) {
  <div class="w-full mt-5 flex flex-col gap-y-5">
    <div class="p-4 border border-gray-200 rounded-lg bg-gray-50 space-y-2 text-sm text-gray-700">
      <p><span class="font-medium">Médecin :</span> {{ doctorName || 'Docteur' }}</p>
      <p><span class="font-medium">Date :</span> {{ slot?.date | date:'EEEE d MMMM yyyy' }} de {{ slot?.startTime | date:'HH:mm' }} à {{ slot?.endTime | date:'HH:mm' }}</p>
      <p><span class="font-medium">Patient :</span> {{ getPatientName(getSelectedPatient()) }}</p>
      <p><span class="font-medium">Motif :</span> {{ appointmentForm.get('reason')?.value }}</p>
    </div>
    <div class="p-4 border border-primary/30 rounded-lg bg-primary/5">
      <p class="text-sm font-medium text-gray-700">
        Acompte à régler : <span class="text-primary font-semibold text-lg">{{ formatAmount(getDepositAmount()) }}</span>
      </p>
    </div>
    <div class="flex gap-3 pt-2 justify-between">
      <button
        type="button"
        (click)="goBackToStep1()"
        class="text-sm px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2">
        <lucide-icon [img]="ChevronLeftIcon" [size]="16"></lucide-icon>
        Retour
      </button>
      <button
        type="button"
        (click)="confirmAndPay()"
        [disabled]="isLoading"
        class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm">
        <div *ngIf="isLoading" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
        {{ isLoading ? 'Paiement...' : 'Payer et confirmer' }}
      </button>
    </div>
  </div>
}

<!-- Étape 3 : Paiement (iframe checkout) -->
@if (currentStep === 3 && checkoutLinkUrl) {
  <div class="w-full mt-5 flex flex-col gap-y-4">
    <p class="text-sm text-gray-600">Completez le paiement dans la fenêtre ci-dessous.</p>
    <div class="border border-gray-200 rounded-lg overflow-hidden bg-white" style="min-height: 500px;">
      <iframe
        [src]="checkoutLinkUrl | safe"
        title="Paiement"
        class="w-full border-0"
        style="height: 500px;">
      </iframe>
    </div>
    <div class="flex justify-end">
      <button
        type="button"
        (click)="closeAfterPayment()"
        class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm">
        J'ai terminé le paiement
      </button>
    </div>
  </div>
}